import { DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { inject, CSP_NONCE, Injectable, InjectionToken, ElementRef, booleanAttribute, Component, ChangeDetectionStrategy, Input, HostBinding, NgModule, Optional, SkipSelf } from '@angular/core';
import { UxaDirectionality } from '@ux-aspects-universal/angular/i18n';
let uniqueId$1 = 0;
class UxaIconProcessorService {
  constructor() {
    this._nonce = inject(CSP_NONCE, {
      optional: true
    });
  }
  preProcessor(icon) {
    // some icons contain an id attribute which will cause issues if multiple icons are rendered on the same page
    // to avoid this as part of pre-processing we replace any ids with a placeholder e.g. uxa-icon-id-1, uxa-icon-id-2 etc.
    // we need to replace these placeholders with unique ids to ensure the icons are rendered correctly
    // however we want to keep the `-1` part of the id in case there are multiple clip paths within the icon
    const id = `uxa-icon-id-${uniqueId$1++}`;
    icon = icon.replace(/uxa-icon-id-(\d+)/g, `${id}-$1`);
    // if there is no nonce value then we don't need to do anything
    if (!this._nonce) {
      return icon;
    }
    // rename all style attributes to data-style to avoid being blocked by the CSP
    return icon.replace(/style\s*=/g, 'data-style=');
  }
  postProcessor(element) {
    // if there is no nonce value then we don't need to do anything
    if (!this._nonce) {
      return;
    }
    // find all elements with a data-style attribute and get the styles from it
    // and apply them to the element using the style property and remove the data-style attribute
    const targets = element.querySelectorAll('[data-style]');
    for (const target of Array.from(targets)) {
      const styles = target.getAttribute('data-style');
      styles?.split(';').forEach(style => {
        const [property, value] = style.split(':');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        target.style[property.trim()] = value.trim();
      });
      target.removeAttribute('data-style');
    }
  }
  static {
    this.ɵfac = function UxaIconProcessorService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaIconProcessorService)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: UxaIconProcessorService,
      factory: UxaIconProcessorService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaIconProcessorService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();
function injectIconProcessor() {
  return inject(UxaIconProcessorService);
}

/**
 * Find an icon by name in the registered icons
 * @private
 */
function resolveIcon(name, icons) {
  // lower kebab case icons are deprecated, use lower camel case instead
  if (name.includes('-')) {
    // get the icon name in lower camel case
    name = name.replace(/-([a-z])/g, g => g[1].toUpperCase());
    console.warn(`Using the lower kebab case format is deprecated. Please use the lower camel case format instead: ${name}.`);
  }
  // we must reverse the list so that icons registered later take precedence
  for (const iconset of [...icons].reverse()) {
    if (iconset[name]) {
      return iconset[name];
    }
  }
  throw new Error(`Icon ${name} was not found. Register it using UxaIconModule.withIcons({ ${name} }) or provideIcons({ ${name} }).`);
}
const UxaIconsToken = new InjectionToken('Icons Token');
let uniqueId = 0;
/**
 * The `uxa-icon` component can be used to display an icon from UX Aspects Universal Iconsets.
 * Icons can also be mirrored for use in RTL layouts where applicable.
 *
 * ## Importing icons
 *
 * Each icon must be explicitly imported and registered with the `UxaIconModule` before it can be used.
 *
 * ```typescript
import { UxaIconModule } from '@ux-aspects-universal/angular/icon';
import { search, addressBook } from '@ux-aspects-universal/angular/icon/ux-aspects';

@NgModule({
  imports: [UxaIconModule.withIcons({ search, addressBook })]
})```
 *
 * ## Using icons
 *
 * ```html
<uxa-icon name="search"></uxa-icon>
<uxa-icon name="addressBook"></uxa-icon>
 ```
 */
class UxaIconComponent {
  constructor() {
    this._document = inject(DOCUMENT);
    this._processor = injectIconProcessor();
    this._iconsets = inject(UxaIconsToken);
    this._elementRef = inject(ElementRef);
    this._directionality = inject(UxaDirectionality);
    /** Whether the icon should be mirrored */
    this.mirror = false;
    /** Whether the icon should be mirrored automatically in RTL mode */
    this.mirrorInRtl = false;
    /** Define if icon is picked up by screen readers */
    this.ariaHidden = false;
    /** Generate unique ids for the SVG elements */
    this._uniqueId = uniqueId++;
  }
  /** Define the icon to display */
  set name(name) {
    const icon = resolveIcon(name, this._iconsets);
    // set the inner html of the icon element
    this._elementRef.nativeElement.innerHTML = this._processor.preProcessor(icon);
    this._processor.postProcessor(this._elementRef.nativeElement);
    this._svg = this._elementRef.nativeElement.querySelector('svg');
  }
  /** @internal Set the transform style based on the mirror property */
  get transform() {
    let shouldMirror = this.mirror;
    if (this.mirrorInRtl) {
      shouldMirror = this._directionality.dir === 'rtl' ? !shouldMirror : shouldMirror;
    }
    return shouldMirror ? 'scaleX(-1)' : undefined;
  }
  ngOnChanges() {
    this.setSvgAttributes();
    this.setTitle();
  }
  /** Adds aria hidden attribute to the SVG element */
  setSvgAttributes() {
    this._svg?.setAttribute('aria-hidden', this.label ? this.ariaHidden.toString() : 'true');
    this._svg?.setAttribute('role', this.role ?? 'img');
  }
  /** Adds title tag inside the SVG element */
  setTitle() {
    if (!this._svg) {
      return;
    }
    if (this.label) {
      const svgElement = this._svg.querySelector('title') ?? this._document.createElementNS('http://www.w3.org/2000/svg', 'title');
      svgElement.textContent = this.label;
      svgElement.setAttribute('id', `uxa-icon-svg-title-${this._uniqueId}`);
      this._svg.prepend(svgElement);
      this._svg?.setAttribute('aria-labelledby', `uxa-icon-svg-title-${this._uniqueId}`);
    } else {
      this._svg?.querySelector('title')?.remove();
      this._svg?.removeAttribute('aria-labelledby');
    }
  }
  static {
    this.ɵfac = function UxaIconComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaIconComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: UxaIconComponent,
      selectors: [["uxa-icon"]],
      hostVars: 6,
      hostBindings: function UxaIconComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵattribute("aria-hidden", null)("role", null);
          i0.ɵɵstyleProp("--uxa-icon__size", ctx.size)("--uxa-icon__transform", ctx.transform);
        }
      },
      inputs: {
        name: "name",
        size: "size",
        mirror: [2, "mirror", "mirror", booleanAttribute],
        mirrorInRtl: [2, "mirrorInRtl", "mirrorInRtl", booleanAttribute],
        ariaHidden: [2, "aria-hidden", "ariaHidden", booleanAttribute],
        label: "label",
        role: "role"
      },
      features: [i0.ɵɵNgOnChangesFeature],
      decls: 0,
      vars: 0,
      template: function UxaIconComponent_Template(rf, ctx) {},
      styles: ["[_nghost-%COMP%]{display:inline-block;width:var(--uxa-icon__size, 1em);height:var(--uxa-icon__size, 1em);font-size:var(--uxa-icon__size, 1em);flex:none;overflow:hidden;line-height:initial}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaIconComponent, [{
    type: Component,
    args: [{
      selector: 'uxa-icon',
      changeDetection: ChangeDetectionStrategy.OnPush,
      host: {
        '[attr.aria-hidden]': 'null',
        '[attr.role]': 'null'
      },
      standalone: true,
      template: "",
      styles: [":host{display:inline-block;width:var(--uxa-icon__size, 1em);height:var(--uxa-icon__size, 1em);font-size:var(--uxa-icon__size, 1em);flex:none;overflow:hidden;line-height:initial}\n"]
    }]
  }], null, {
    name: [{
      type: Input
    }],
    size: [{
      type: HostBinding,
      args: ['style.--uxa-icon__size']
    }, {
      type: Input
    }],
    mirror: [{
      type: Input,
      args: [{
        transform: booleanAttribute
      }]
    }],
    mirrorInRtl: [{
      type: Input,
      args: [{
        transform: booleanAttribute
      }]
    }],
    ariaHidden: [{
      type: Input,
      args: [{
        alias: 'aria-hidden',
        transform: booleanAttribute
      }]
    }],
    label: [{
      type: Input
    }],
    role: [{
      type: Input
    }],
    transform: [{
      type: HostBinding,
      args: ['style.--uxa-icon__transform']
    }]
  });
})();
class UxaIconModule {
  static withIcons(icons) {
    return {
      ngModule: UxaIconModule,
      providers: provideIcons(icons)
    };
  }
  static {
    this.ɵfac = function UxaIconModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaIconModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: UxaIconModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaIconModule, [{
    type: NgModule,
    args: [{
      imports: [UxaIconComponent],
      exports: [UxaIconComponent]
    }]
  }], null, null);
})();
/**
 * Register icons using the `providers` array either in `bootstrapApplication` or
 * in the @Component `providers` array.
 * @param icons The icons to provide
 */
function provideIcons(icons) {
  return [{
    provide: UxaIconsToken,
    useFactory: parentIcons => ({
      ...parentIcons?.reduce((acc, iconset) => ({
        ...acc,
        ...iconset
      }), {}),
      ...icons
    }),
    deps: [[UxaIconsToken, new Optional(), new SkipSelf()]],
    multi: true
  }];
}

/**
 * Generated bundle index. Do not edit.
 */

export { UxaIconComponent, UxaIconModule, provideIcons, resolveIcon };
